/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description getCommonEntityData
 */

import { selectShowNightlyPrice } from '@zcode-ui/filters'
import { PropertyFeatures, PropertyType } from '@zcode/enums'
import { US_STATES, splitOnMiddleDot } from '@zcode/nr-utils'
import { checkAddressHidden } from '@zcode/nr/es/shared/utils/address'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import { currencyFormatter, isTruthy } from '@zcode/utils'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getPropertyTypeTitle } from './getPropertyTypeTitle'

const EXCLUDED_PROPERTY_TYPES = [
  PropertyType.APARTMENT_CONDO_TOWNHOUSE,
  PropertyType.FLOOR_PLAN,
  PropertyType.INCOME_INVESTMENT,
  PropertyType.MOBILE_MANUFACTURED,
  PropertyType.TIC,
  PropertyType.UNKNOWN,
]

export const useGetCommonEntityData = () => {
  const shouldShowNightlyPrice = useSelector(selectShowNightlyPrice)
  return useCallback(
    ({
      addCountryInAddress,
      entity,
      isShortTerm,
      showShortPrice,
    }: {
      addCountryInAddress?: boolean
      entity: Listable | Pin<RawPinInventorySearch>
      isShortTerm?: boolean
      showShortPrice?: boolean
    }) => {
      const {
        bathroomText,
        bedroomText,
        bedsBathsRangeTxt,
        buildingName,
        city,
        displayableAddress,
        features,
        headerText,
        isBuilding,
        isUnavailable,
        minPriceText,
        neighborhoodName,
        pricePerNight,
        previousPrice,
        priceText,
        priceTxt,
        propertyType,
        state,
        title,
        zipcode,
      } = entity

      let country: string | undefined

      if (state) {
        country = US_STATES[state as keyof typeof US_STATES] ? 'USA' : 'CAN'
      }

      const cityAndHood = [neighborhoodName, city].filter(isTruthy).join(' · ')

      const displayableZipcode = zipcode == null ? '' : ` ${zipcode}`
      const displayableCityStateZip = `${[city, state]
        .filter(isTruthy)
        .join(', ')}${displayableZipcode}`

      const displayableCityStateZipCountry = `${displayableCityStateZip}${country ? `, ${country}` : ''}`

      const cityStateZipToDisplay = addCountryInAddress
        ? displayableCityStateZipCountry
        : displayableCityStateZip

      let fullAddress = displayableAddress
        ? `${displayableAddress}, ${cityStateZipToDisplay}`
        : cityStateZipToDisplay

      if (checkAddressHidden(displayableAddress)) {
        const prefix = bathroomText ? `${bedroomText} & ${bathroomText}` : bedroomText

        fullAddress = prefix ? `${prefix} in ${cityStateZipToDisplay}` : cityStateZipToDisplay
      }

      const [bedsRange, bathsRange] = splitOnMiddleDot(bedsBathsRangeTxt.replaceAll(' - ', '–'))
      const hasNoPrice = priceTxt === 'N/A' && isUnavailable
      let price: 'Call for pricing' | 'Call for price' | typeof priceTxt | typeof priceText
      if (
        hasNoPrice ||
        (priceTxt === 'N/A' && features?.includes(PropertyFeatures.USES_WAITLIST))
      ) {
        price = isShortTerm ? 'Call for pricing' : 'Call for price'
      } else {
        price = showShortPrice ? priceTxt : priceText
      }
      let previousPriceText
      if (previousPrice) {
        previousPriceText = currencyFormatter(previousPrice, { showCents: false, symbol: '$' })
      }
      const propertyTypeTitle = getPropertyTypeTitle(propertyType)
      let shortTermPropertyTypeText = 'Apartment'
      if (propertyTypeTitle && propertyType && !EXCLUDED_PROPERTY_TYPES.includes(propertyType)) {
        shortTermPropertyTypeText = propertyTypeTitle
      }

      const shortTermPropertyText = neighborhoodName
        ? `${shortTermPropertyTypeText} in ${neighborhoodName}`
        : shortTermPropertyTypeText

      const showPricePerNight = shouldShowNightlyPrice && !!pricePerNight
      const shortTermPrice = showPricePerNight ? pricePerNight : minPriceText

      const defaultHeaderText = title || headerText
      const shortTermHeaderText = isBuilding ? buildingName || defaultHeaderText : defaultHeaderText

      return {
        bedsRange,
        bathsRange,
        cityAndHood,
        fullAddress,
        price,
        previousPriceText,
        shortTermHeaderText,
        shortTermPrice,
        shortTermPropertyText,
        showPricePerNight,
      }
    },
    [shouldShowNightlyPrice]
  )
}
